import { asAccountId } from "@repo/app-types/entities/account";
import {
  asPlaybookId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import {
  buildTeammateDisplayNames,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import {
  CustomerTimeline,
  type CustomerTimelineActivity,
} from "@repo/ui/customerTimeline/customerTimeline";
import { CustomerTimelineCall } from "@repo/ui/customerTimeline/customerTimelineCall";
import { FilterBar } from "@repo/ui/Filters/filterBar";
import { Size } from "@repo/ui/helpers/helpers";
import { useInfiniteScroll } from "@repo/ui/helpers/useInfiniteScroll";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { teammateLabelKind } from "@repo/ui/teammate/teammateLabel";
import { IconPhone } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useMemo, useState, type FC } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  useCustomerDetailQuery,
  useCustomerTimelineInfiniteQuery,
} from "../../api/entities/customer.js";
import { appPaths } from "../../app/appPaths.js";
import { requireRouteParam } from "../../app/routeParams.js";
import { PageBody, PageLayout } from "../../components/layout/pageLayout.js";
import {
  NavigationSlideoverContainer,
  useModalNavigation,
} from "../../hooks/useModalNavigation.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { renderCallLength } from "../teammatePerformance/durationFormatting.js";
import { GradingSectionSummariesContainer } from "../teammatePerformance/gradingSectionSummariesContainer.js";
import { CustomerPlaybookScores } from "./customerPlaybookScores.js";

export const CustomerDetailPage: FC = () => {
  const routeParams = useParams();
  const accountId = asAccountId(requireRouteParam(routeParams, "accountId"));
  const playbookId = asPlaybookId(requireRouteParam(routeParams, "playbookId"));
  const [selectedTeammateId, setSelectedTeammateId] =
    useState<TeammateId | null>(null);
  const [selectedPlaybookId, setSelectedPlaybookId] =
    useState<PlaybookId | null>(null);
  const detailResult = useCustomerDetailQuery(accountId);
  const timelineResult = useCustomerTimelineInfiniteQuery(accountId);
  const detail = detailResult.currentData;
  const pages = timelineResult.currentData?.pages;
  const playbookScores = detail?.playbook_scores;
  const calls = useMemo(
    () => pages?.flatMap((page) => page.calls) ?? [],
    [pages],
  );
  const { loadMoreSentinelRef } = useInfiniteScroll({
    entities: calls,
    onLoadMore:
      timelineResult.hasNextPage &&
      !timelineResult.isFetching &&
      !timelineResult.isError
        ? timelineResult.fetchNextPage
        : undefined,
  });
  const playbookOptions = useMemo<
    readonly SimpleSelectOption<PlaybookId | "all">[]
  >(
    () => [
      { value: "all", label: "All playbooks" },
      ...(detail?.playbooks ?? [])
        .map((playbook) => ({ value: playbook.id, label: playbook.name }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    ],
    [detail?.playbooks],
  );
  const teammateOptions = useMemo<
    readonly SimpleSelectOption<TeammateId | "all">[]
  >(() => {
    return [
      { value: "all", label: "All teammates" },
      ...(detail?.teammates ?? [])
        .map((teammate) => ({
          value: teammate.id,
          label: buildTeammateDisplayNames(teammate).primaryDisplayName,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [detail?.teammates]);

  const highlightedActivityIds = useMemo(() => {
    if (selectedPlaybookId === null && selectedTeammateId === null) {
      return undefined;
    }
    return new Set(
      calls
        .filter(
          (call) =>
            (selectedPlaybookId === null ||
              call.playbook_id === selectedPlaybookId) &&
            (selectedTeammateId === null ||
              call.teammates.some(
                (teammate) => teammate.id === selectedTeammateId,
              )),
        )
        .map((call) => call.id),
    );
  }, [calls, selectedPlaybookId, selectedTeammateId]);

  const modalNavigation = useModalNavigation();

  const activities: readonly CustomerTimelineActivity[] = calls.map((call) => ({
    id: call.id,
    dateTime: call.recorded_at,
    dateLabel: DateTime.fromISO(call.recorded_at).toFormat("MMM d, h:mm a"),
    icon: <IconPhone className="size-4" />,
    content: (
      <div className={PH_MASK_CLASS}>
        <CustomerTimelineCall
          title={call.title || "Call recording"}
          openCall={() =>
            modalNavigation.push({
              pathname: appPaths.playbookCustomerCallTranscript.build({
                playbookId,
                accountId,
                gradingId: call.grading_id,
              }),
              search: new URLSearchParams({
                recordingId: call.id,
              }).toString(),
            })
          }
          participants={call.teammates.map((teammate) => ({
            id: teammate.id,
            name: buildTeammateDisplayNames(teammate).primaryDisplayName,
            kind: teammateLabelKind(teammate.type),
          }))}
          duration={
            call.duration_seconds == null
              ? undefined
              : renderCallLength(call.duration_seconds)
          }
          gradingSummary={
            call.assessment ? (
              <GradingSectionSummariesContainer assessment={call.assessment} />
            ) : undefined
          }
        />
      </div>
    ),
  }));

  const isError =
    (detailResult.isError && !detail) || (timelineResult.isError && !pages);

  const renderTimeline = () => {
    if (isError) {
      return (
        <p className="text-sm text-muted-foreground">
          Unable to load customer.
        </p>
      );
    }

    if (!pages) {
      return <p className="text-sm text-muted-foreground">Loading timeline…</p>;
    }

    return (
      <>
        <CustomerTimeline
          activities={activities}
          highlightedActivityIds={highlightedActivityIds}
        />
        {timelineResult.isError ? (
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.SMALL}
            className="mt-5"
            onClick={() => void timelineResult.fetchNextPage()}
          >
            Retry loading activity
          </Button>
        ) : timelineResult.hasNextPage ? (
          <div ref={loadMoreSentinelRef} className="mt-5 min-h-8" role="status">
            {timelineResult.isFetchingNextPage
              ? "Loading more activity…"
              : null}
          </div>
        ) : null}
      </>
    );
  };

  return (
    <PageLayout>
      <PageHeader
        title={
          <span className={PH_MASK_CLASS}>{detail?.name ?? "Customer"}</span>
        }
        isLoading={detailResult.isLoading}
        filterBar={
          <FilterBar
            isLoading={
              (timelineResult.isFetching &&
                !timelineResult.isFetchingNextPage) ||
              detailResult.isFetching
            }
            leadingItems={
              <SimpleSelect
                label="Playbook"
                isLabelHidden
                isDisabled={!detail}
                options={playbookOptions}
                value={selectedPlaybookId ?? "all"}
                onChange={(value) =>
                  setSelectedPlaybookId(value === "all" ? null : value)
                }
                size={Size.SMALL}
              />
            }
            items={
              <div className={twMerge("contents", PH_MASK_CLASS)}>
                <SimpleSelect
                  label="Teammate"
                  isLabelHidden
                  isDisabled={!detail}
                  options={teammateOptions}
                  value={selectedTeammateId ?? "all"}
                  onChange={(value) =>
                    setSelectedTeammateId(value === "all" ? null : value)
                  }
                  size={Size.SMALL}
                />
              </div>
            }
            onReset={
              selectedTeammateId !== null || selectedPlaybookId !== null
                ? () => {
                    setSelectedTeammateId(null);
                    setSelectedPlaybookId(null);
                  }
                : undefined
            }
          />
        }
      />
      <PageBody>
        {playbookScores && (
          <CustomerPlaybookScores
            scores={playbookScores}
            selectedPlaybookId={selectedPlaybookId}
            selectedTeammateId={selectedTeammateId}
            onSelect={(playbookId, teammateId) => {
              setSelectedPlaybookId(playbookId);
              setSelectedTeammateId(teammateId);
            }}
          />
        )}
        {/* Where the list starts beneath the pinned header: the title row, the
            filter bar, and the body's top padding. */}
        <div className="[--customer-timeline-sticky-top:6.5rem] sm:[--customer-timeline-sticky-top:7.5rem]">
          {renderTimeline()}
        </div>
      </PageBody>
      <NavigationSlideoverContainer />
    </PageLayout>
  );
};
